import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/api/supabase'
import type { EmailOtpType } from '@supabase/supabase-js'
import { toast } from '@/hooks/use-toast'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'

export default function AuthConfirmPage() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleVerification = async () => {
    const token_hash = searchParams.get('token_hash')
    const type = searchParams.get('type') as EmailOtpType | null
    const next = searchParams.get('next') ?? '/'

    if (!token_hash || !type) {
      setError('Invalid verification link')
      toast({
        title: "Invalid Link",
        description: "This verification link is invalid or has expired.",
        variant: "destructive"
      })
      return
    }

    try {
      setLoading(true)
      const { error, data } = await supabase.auth.verifyOtp({
        type,
        token_hash,
      })
      if (error) throw error

      // If verification successful, redirect to the next page
      navigate(next)
    } catch (error) {
      console.error('Verification error:', error)
      setError('Failed to verify email link')
      toast({
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "Failed to verify email link",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  // Show error state if verification fails
  if (error) {
    return (
      <div className="container flex items-center justify-center min-h-[100dvh] px-4 py-8 md:py-12">
        <Card className="w-full max-w-[380px] p-4 md:p-8">
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <Icons.warning className="h-12 w-12 text-destructive" />
            </div>
            <h1 className="text-xl md:text-2xl font-semibold text-destructive">Verification Failed</h1>
            <p className="text-sm md:text-base text-muted-foreground">{error}</p>
          </div>
          <Button 
            className="w-full mt-6"
            onClick={() => navigate('/')}
          >
            Return to Home
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="container flex items-center justify-center min-h-[100dvh] px-4 py-8 md:py-12">
      <Card className="w-full max-w-[380px] p-4 md:p-8">
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <Icons.lock className="h-12 w-12 text-primary" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl md:text-2xl font-semibold">Reset Your Password</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Your email has been verified. Click continue to set up your new password.
            </p>
          </div>
        </div>
        <Button 
          className="w-full mt-6"
          onClick={handleVerification}
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            "Continue to Reset Password"
          )}
        </Button>
      </Card>
    </div>
  )
} 